import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { can, fieldVisible, type Me } from '../auth/me';
import { parsePln, plnToInput } from '../catalog/money';
import { ConflictNotice } from '../ui/ConflictNotice';
import { CONTRACTS_RESOURCE, KINDS, type Contract, type ContractKind, type Template } from './types';
import { sameValues, useOpening, useUnsavedGuard, validForm } from '../ui/unsaved';

type PersonOption = { id: string; name: string };
type EventOption = { id: string; name: string; startsAt: string };

const warsawToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Warsaw' }).format(new Date());
const shiftDays = (day: string, days: number) => new Date(Date.parse(`${day}T12:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);

/**
 * A new contract (person, kind, template, subject, amount, period, optionally an event) or the changes of an existing
 * one (only the changed fields are sent — rule 7a). `personId` fixed: made from a person's card.
 */
export function ContractForm({ me, contract, personId, onSaved, onClose }: { me: Me; contract?: Contract; personId?: string; onSaved: (c: Contract) => void; onClose: () => void }) {
  const today = warsawToday();
  const [kind, setKind] = useState<ContractKind>(contract?.kind ?? 'dzielo');
  const [person, setPerson] = useState(contract?.personId ?? personId ?? '');
  const [eventId, setEventId] = useState(contract?.eventId ?? '');
  const [templateId, setTemplateId] = useState(contract?.templateId ?? '');
  const [title, setTitle] = useState(contract?.title ?? '');
  const [subject, setSubject] = useState(contract?.subject ?? '');
  const amountVisible = fieldVisible(me, CONTRACTS_RESOURCE, 'amountGrosze');
  const [amount, setAmount] = useState(contract?.amountGrosze !== undefined ? plnToInput(contract.amountGrosze) : '');
  const [dateFrom, setDateFrom] = useState(contract?.dateFrom ?? today);
  const [dateTo, setDateTo] = useState(contract?.dateTo ?? today);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const persons = useQuery({
    queryKey: ['people', 'persons', 'contracts'],
    enabled: !contract && !personId,
    queryFn: () => api<{ persons: PersonOption[] }>('/api/people/persons'),
  });
  const events = useQuery({
    queryKey: ['events', 'contracts', today],
    enabled: can(me, 'events.events', 'view'),
    queryFn: () => api<{ events: EventOption[] }>(`/api/events?from=${shiftDays(today, -120)}&to=${shiftDays(today, 240)}`),
  });
  const templates = useQuery({ queryKey: ['contracts', 'templates'], enabled: can(me, 'contracts.templates', 'view'), queryFn: () => api<{ templates: Template[] }>('/api/contracts/templates') });
  const ofKind = (templates.data?.templates ?? []).filter((t) => t.kind === kind);

  /** Saves; false when it did not (the reason is shown). */
  async function persist(): Promise<boolean> {
    setError(null);
    let amountGrosze: number | undefined;
    try {
      if (amountVisible) amountGrosze = parsePln(amount);
    } catch (err) {
      setError(err);
      return false;
    }
    const values: Record<string, unknown> = {
      eventId: eventId || null,
      ...(templateId ? { templateId } : {}),
      ...(title.trim() ? { title: title.trim() } : {}),
      subject: subject.trim(),
      ...(amountGrosze !== undefined ? { amountGrosze } : {}),
      dateFrom,
      dateTo,
    };
    setBusy(true);
    try {
      if (contract) {
        const before: Record<string, unknown> = { eventId: contract.eventId, templateId: contract.templateId, title: contract.title, subject: contract.subject, amountGrosze: contract.amountGrosze, dateFrom: contract.dateFrom, dateTo: contract.dateTo };
        const changed = Object.fromEntries(Object.entries(values).filter(([k, v]) => before[k] !== v));
        onSaved(Object.keys(changed).length === 0 ? contract : await api<Contract>(`/api/contracts/${contract.id}`, { method: 'PATCH', body: { ...changed, version: contract.version } }));
      } else {
        onSaved(await api<Contract>('/api/contracts', { method: 'POST', body: { kind, personId: person, ...values } }));
      }
      return true;
    } catch (err) {
      setError(err);
      return false;
    } finally {
      setBusy(false);
    }
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    void persist();
  }
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const now = { kind, person, eventId, templateId, title, subject, amount, dateFrom, dateTo };
  const opening = useOpening(now);
  const guardRef = useUnsavedGuard({ dirty: !sameValues(now, opening), save: async (el) => validForm(el) && persist() });

  const issue = (path: string) => (error instanceof ApiError ? fieldIssues(error).find((i) => i.path === path)?.message : undefined);
  return (
    <form ref={guardRef} className="ctr-form" onSubmit={submit} aria-label={contract ? `Zmiana umowy ${contract.number}` : 'Nowa umowa'} data-feature-id="contracts.list">
      <h2>{contract ? `Zmiana umowy ${contract.number}` : 'Nowa umowa'}</h2>
      {error instanceof ApiError && error.status === 409 && <ConflictNotice onReload={onClose} />}
      {error !== null && !(error instanceof ApiError && (error.status === 409 || fieldIssues(error).length > 0)) && (
        <p className="alert" role="alert">
          {error instanceof Error ? error.message : 'Nie udało się zapisać umowy'}
        </p>
      )}
      <div className="ctr-grid">
        {!contract && !personId && (
          <label className="field">
            <span>Osoba</span>
            <select required value={person} onChange={(e) => setPerson(e.target.value)}>
              <option value="">— wybierz —</option>
              {(persons.data?.persons ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        )}
        {!contract && (
          <label className="field">
            <span>Rodzaj</span>
            <select
              value={kind}
              onChange={(e) => {
                setKind(e.target.value as ContractKind);
                setTemplateId('');
              }}
            >
              {(Object.keys(KINDS) as ContractKind[]).map((k) => (
                <option key={k} value={k}>
                  {KINDS[k]}
                </option>
              ))}
            </select>
          </label>
        )}
        {ofKind.length > 1 && (
          <label className="field">
            <span>Szablon</span>
            <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
              <option value="">Domyślny</option>
              {ofKind.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="field">
          <span>Tytuł (opcjonalnie)</span>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={KINDS[kind]} maxLength={200} />
        </label>
        {events.data && (
          <label className="field">
            <span>Wydarzenie (opcjonalnie)</span>
            <select value={eventId} onChange={(e) => setEventId(e.target.value)}>
              <option value="">— bez wydarzenia —</option>
              {contract?.eventId && !events.data.events.some((ev) => ev.id === contract.eventId) && <option value={contract.eventId}>{contract.eventName}</option>}
              {events.data.events.map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.startsAt.slice(0, 10)} {ev.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="field ctr-wide">
          <span>Przedmiot umowy</span>
          <textarea required rows={3} value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={5000} />
          {issue('subject') && <small className="field-error">{issue('subject')}</small>}
        </label>
        {amountVisible && (
          <label className="field">
            <span>Kwota brutto (zł)</span>
            <input required inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0,00" />
            {issue('amountGrosze') && <small className="field-error">{issue('amountGrosze')}</small>}
          </label>
        )}
        <label className="field">
          <span>Od</span>
          <input type="date" required value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </label>
        <label className="field">
          <span>Do</span>
          <input type="date" required value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          {issue('dateTo') && <small className="field-error">{issue('dateTo')}</small>}
        </label>
      </div>
      <div className="actions">
        <button className="btn primary" type="submit" disabled={busy}>
          {busy ? 'Zapisywanie…' : contract ? 'Zapisz zmiany' : 'Utwórz umowę'}
        </button>
        <button className="btn" type="button" onClick={onClose}>
          Anuluj
        </button>
      </div>
    </form>
  );
}
